import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent, RefObject } from 'react';
import { useChromeLayout } from './ChromeContext';

export interface Popover {
  open: boolean;
  toggle: () => void;
  close: () => void;
  /** On the trigger: a press on it must not count as a press outside the open panel. */
  keepOpenOnPress: (event: MouseEvent) => void;
  /** For the trigger's Tooltip `open`: shut while the panel is open, so it can't cover the panel. */
  tooltipOpen: false | undefined;
}

/**
 * Open state for one of the viewer's menus or panels. With a `panelRef` it also
 * closes on Esc and on a press outside both panel and trigger; a ChipMenu does
 * that itself.
 */
export function usePopover(
  anchorRef: RefObject<HTMLElement | null>,
  panelRef?: RefObject<HTMLElement | null>,
): Popover {
  const [open, setOpen] = useState(false);
  const { openPopoversRef } = useChromeLayout();
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((current) => !current), []);
  const keepOpenOnPress = useCallback((event: MouseEvent) => event.stopPropagation(), []);
  // How many of the viewer's popovers were open, this one included, when it opened.
  const layer = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    openPopoversRef.current += 1;
    layer.current = openPopoversRef.current;
    return () => {
      openPopoversRef.current -= 1;
    };
  }, [open, openPopoversRef]);

  useEffect(() => {
    if (!open || !panelRef) return undefined;
    const onKey = (event: KeyboardEvent) => {
      // A menu opened inside the panel takes the Esc first.
      if (event.key === 'Escape' && openPopoversRef.current <= layer.current) close();
    };
    const onDown = (event: PointerEvent) => {
      const path = event.composedPath();
      const inside = [panelRef.current, anchorRef.current].some((node) => node !== null && path.includes(node));
      if (!inside) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, anchorRef, panelRef, close]);

  return { open, toggle, close, keepOpenOnPress, tooltipOpen: open ? false : undefined };
}
